import { QueryClient, useQuery } from '@tanstack/react-query';
import {
   getCourseAssignmentOperatorDetailsFromClient,
   getCourseAssignmentOperatorDetails,
   GetCourseAssignmentOperatorDetailsErrorCode,
} from '~/pages/api/courses/getCourseAssignmentOperatorDetails.api';
import { ApiClient } from '@dozuki/web-js/utils';
import { useLocalization } from '~/framework';
import { useApiErrorMessageToast } from '~/pages/_shared';

const KEY = 'course-assignment-operator';

export function getCourseAssignmentOperatorDetailsQueryKey(courseAssignmentId: number | null) {
   return [KEY, courseAssignmentId];
}

export function useCourseAssignmentOperatorDetails(
   courseAssignmentId: number | null,
   limitsEnabled: boolean = true
) {
   const { _js } = useLocalization();
   const { handleErrorOrRedirectToLogin } =
      useApiErrorMessageToast<GetCourseAssignmentOperatorDetailsErrorCode>({
         'courses.course_assignment_details_not_found': _js(
            'Could not find course assignment details'
         ),
         'course_assignments.get_operator_course_assignment_details.not_assignee': _js(
            'You are not an assignee on this course assignment'
         ),
         default: _js('There was a problem retrieving the selected course assignment details'),
      });

   const queryFunction = async () => {
      if (!limitsEnabled || !courseAssignmentId) {
         return;
      }
      return await getCourseAssignmentOperatorDetailsFromClient(courseAssignmentId);
   };

   return useQuery({
      queryFn: queryFunction,
      queryKey: getCourseAssignmentOperatorDetailsQueryKey(courseAssignmentId),
      onError: handleErrorOrRedirectToLogin,
   });
}

export async function prefetchCourseAssignmentOperatorDetails(
   courseAssignmentId: number,
   apiClient: ApiClient,
   queryClient: QueryClient
): Promise<void> {
   await queryClient.fetchQuery([KEY, courseAssignmentId], () =>
      getCourseAssignmentOperatorDetails(courseAssignmentId, { apiClient })
   );
}
